import { PreferredSizes } from '@/data/types/PreferredSizes';
import { useCallback, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { z } from 'zod';

const fetcher = async () => {
	const response = await fetch('/api/preferred_sizes');

	if (!response.ok) {
		throw new Error('Network response was not ok');
	}

	const data = await response.json();

	return data as PreferredSizes;
};

const GET_PREFERRED_SIZES_DATA_KEY = 'GET_PREFERRED_SIZES_DATA_KEY';

export const useGetPreferredSizes = () => {
	const baseQuery = useSWR(GET_PREFERRED_SIZES_DATA_KEY, fetcher);
	return baseQuery;
};

const UserPreferredSizesSchema = z.object({
	label: z.string(),
	value: z.string(),
});

const UserPreferredSizesArraySchema = z.array(UserPreferredSizesSchema);

export const parseUserPreferredSizes = (x_userProfileField1: string | null | undefined) => {
	if (!x_userProfileField1) {
		return [];
	}

	try {
		const sizes = JSON.parse(x_userProfileField1);
		const parsedSizes = UserPreferredSizesArraySchema.safeParse(sizes);
		if (parsedSizes.data) {
			return parsedSizes.data;
		}
	} catch (error) {
		console.error('Error parsing x_userProfileField1:', error);
	}

	return [];
};

export const usePreferredSizes = () => {
	const { data: preferredSizes = {}, isLoading: isLoadingPreferredSizes } = useGetPreferredSizes();
	const initPreferredSizeRef = useRef(false);
	const [selectedPreferredSize, setSelectedPreferredSize] = useState<string | null>(null);
	const [selectedSizes, setSelectedSizes] = useState<{ label: string; value: string }[]>([]);

	const selectedSizesPreferredSize = useMemo(
		() => (selectedPreferredSize ? preferredSizes[selectedPreferredSize] : null),
		[selectedPreferredSize, preferredSizes]
	);

	const onSelectPreferredSize = useCallback(
		(size: string | null) => () => {
			setSelectedPreferredSize((prev) => (prev === size ? null : size));
		},
		[]
	);

	const onChangePreferredSize = useCallback(
		(event: any) => {
			const { value } = event.target;
			if (selectedPreferredSize) {
				if (value === '') {
					setSelectedSizes((prev) => prev.filter((item) => item.label !== selectedPreferredSize));
				} else {
					setSelectedSizes((prev) => {
						const existingIndex = prev.findIndex((item) => item.label === selectedPreferredSize);
						if (existingIndex !== -1) {
							const newSizes = [...prev];
							newSizes[existingIndex] = { label: selectedPreferredSize, value };
							return newSizes;
						}
						return [...prev, { label: selectedPreferredSize, value }];
					});
				}
			}
		},
		[selectedPreferredSize]
	);

	const onInitPreferredSize = useCallback((x_userProfileField1: string | null | undefined) => {
		if (!initPreferredSizeRef.current && x_userProfileField1) {
			initPreferredSizeRef.current = true;
			const sizes = parseUserPreferredSizes(x_userProfileField1);
			setSelectedSizes(sizes);
		}
	}, []);

	return {
		selectedSizes,
		selectedSizesPreferredSize,
		selectedPreferredSize,
		preferredSizes,
		isLoadingPreferredSizes,
		onSelectPreferredSize,
		onChangePreferredSize,
		onInitPreferredSize,
	};
};
